import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import {
	Box,
	Collapse,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Skeleton,
	Stack,
	Tooltip,
} from "@mui/material";
import { CodeListFileDto } from "Infrastructure/Api/Api";
import { useCallback, useMemo, useState } from "react";

type FolderNode = {
	name: string;
	path: string;
	children: Map<string, FolderNode>;
};

type Props = {
	files: CodeListFileDto[];
	selectedFolder: string | null;
	onFolderSelect: (folderPath: string) => void;
	isLoading?: boolean;
};

function buildFolderTree(files: CodeListFileDto[]): FolderNode {
	const root: FolderNode = {
		name: "codelists",
		path: "codelists",
		children: new Map(),
	};

	for (const file of files) {
		const parts = file.folderPath.split("/").filter(Boolean);
		let current = root;

		// Skip the first part if it's "codelists" since that's already the root
		const startIndex = parts[0] === "codelists" ? 1 : 0;

		for (let i = startIndex; i < parts.length; i++) {
			const part = parts[i];
			// Build path including "codelists" prefix for consistency
			const currentPath = `codelists/${parts
				.slice(startIndex, i + 1)
				.join("/")}`;

			if (!current.children.has(part)) {
				current.children.set(part, {
					name: part,
					path: currentPath,
					children: new Map(),
				});
			}
			current = current.children.get(part)!;
		}
	}

	return root;
}

function collectAllFolderPaths(node: FolderNode): string[] {
	const paths: string[] = [node.path];
	for (const child of node.children.values()) {
		paths.push(...collectAllFolderPaths(child));
	}
	return paths;
}

type FolderItemProps = {
	node: FolderNode;
	level: number;
	selectedFolder: string | null;
	expandedFolders: Set<string>;
	onFolderSelect: (folderPath: string) => void;
	onToggleExpand: (folderPath: string) => void;
};

const FolderItem: React.FC<FolderItemProps> = ({
	node,
	level,
	selectedFolder,
	expandedFolders,
	onFolderSelect,
	onToggleExpand,
}) => {
	const hasChildren = node.children.size > 0;
	const isExpanded = expandedFolders.has(node.path);
	const isSelected = selectedFolder === node.path;

	const handleSelect = () => {
		onFolderSelect(node.path);
		if (hasChildren && !isExpanded) {
			onToggleExpand(node.path);
		}
	};

	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		onToggleExpand(node.path);
	};

	return (
		<>
			<ListItemButton
				onClick={handleSelect}
				selected={isSelected}
				sx={{
					pl: 2 + level * 2,
					py: 0.5,
					"&.Mui-selected": {
						backgroundColor: "action.selected",
						"&:hover": {
							backgroundColor: "action.selected",
						},
					},
				}}
			>
				<Box
					onClick={hasChildren ? handleToggle : undefined}
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						width: 32,
						height: 32,
						marginLeft: -0.5,
						marginRight: 0.5,
						borderRadius: 1,
						cursor: hasChildren ? "pointer" : "default",
						"&:hover": hasChildren
							? { backgroundColor: "action.hover" }
							: undefined,
					}}
				>
					{hasChildren &&
						(isExpanded ? (
							<ExpandMoreIcon fontSize="small" />
						) : (
							<ChevronRightIcon fontSize="small" />
						))}
				</Box>
				<ListItemIcon sx={{ minWidth: 32 }}>
					<FolderOutlinedIcon fontSize="small" color="action" />
				</ListItemIcon>
				<ListItemText
					primary={node.name}
					primaryTypographyProps={{
						fontSize: "0.875rem",
						fontWeight: isSelected ? 600 : 400,
					}}
				/>
			</ListItemButton>
			{hasChildren && (
				<Collapse in={isExpanded} timeout="auto" unmountOnExit>
					<List component="div" disablePadding>
						{Array.from(node.children.values())
							.sort((a, b) => a.name.localeCompare(b.name))
							.map((child) => (
								<FolderItem
									key={child.path}
									node={child}
									level={level + 1}
									selectedFolder={selectedFolder}
									expandedFolders={expandedFolders}
									onFolderSelect={onFolderSelect}
									onToggleExpand={onToggleExpand}
								/>
							))}
					</List>
				</Collapse>
			)}
		</>
	);
};

export const FolderTree: React.FC<Props> = ({
	files,
	selectedFolder,
	onFolderSelect,
	isLoading,
}) => {
	const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
		new Set(["codelists"]),
	);

	const folderTree = useMemo(() => buildFolderTree(files), [files]);

	const allFolderPaths = useMemo(
		() => collectAllFolderPaths(folderTree),
		[folderTree],
	);

	const handleToggleExpand = useCallback((folderPath: string) => {
		setExpandedFolders((prev) => {
			const next = new Set(prev);
			if (next.has(folderPath)) {
				next.delete(folderPath);
			} else {
				next.add(folderPath);
			}
			return next;
		});
	}, []);

	const handleExpandAll = useCallback(() => {
		setExpandedFolders(new Set(allFolderPaths));
	}, [allFolderPaths]);

	const handleCollapseAll = useCallback(() => {
		setExpandedFolders(new Set(["codelists"]));
	}, []);

	if (isLoading) {
		return (
			<Box sx={{ width: 280, flexShrink: 0, p: 2 }}>
				{[1, 2, 3, 4, 5].map((i) => (
					<Skeleton key={i} height={36} sx={{ mb: 0.5 }} />
				))}
			</Box>
		);
	}

	return (
		<Box
			sx={{
				width: 280,
				flexShrink: 0,
				borderRight: 1,
				borderColor: "divider",
				height: "100%",
				display: "flex",
				flexDirection: "column",
			}}
		>
			<Stack
				direction="row"
				justifyContent="flex-start"
				alignItems="center"
				sx={{
					px: 1,
					py: 0.5,
					borderBottom: 1,
					borderColor: "divider",
					flexShrink: 0,
				}}
			>
				<Tooltip title="Rozbalit vše">
					<IconButton size="small" onClick={handleExpandAll}>
						<UnfoldMoreIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Sbalit vše">
					<IconButton size="small" onClick={handleCollapseAll}>
						<UnfoldLessIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Stack>
			<Box sx={{ overflow: "auto", flex: 1 }}>
				<List component="nav" dense>
					<FolderItem
						node={folderTree}
						level={0}
						selectedFolder={selectedFolder}
						expandedFolders={expandedFolders}
						onFolderSelect={onFolderSelect}
						onToggleExpand={handleToggleExpand}
					/>
				</List>
			</Box>
		</Box>
	);
};
